import { create } from '@bufbuild/protobuf'
import { createClient } from '@connectrpc/connect'
import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { QueryClient, useQueryClient, useQuery as useTanstackQuery } from '@tanstack/react-query'

import { getControlQueryKey, getControlTimelineQueryKey } from '@/api/add-comment.hook'
import { getGetCompanyQueryKey } from '@/api/company.hook'
import {
  ASSESSMENT_QUESTIONNAIRES_BATCH_SIZE,
  ASSESSMENT_QUESTIONNAIRES_BATCH_WINDOW_MS,
  ASSESSMENT_QUESTIONNAIRES_BULK_STALE_TIME_MS,
  ASSESSMENT_QUESTIONNAIRES_MAX_CONCURRENT_BATCHES,
  REFETCH_INTERVAL_MS,
} from '@/api/consts'
import { getListQuestionnairesQueryKey } from '@/api/optimistic-update-questionnaire.hook'
import { Company } from '@/gen/inventory/v1/company_service_pb'
import {
  deleteQuestionnaire,
  getInfoQuestionnaire,
  getPortalCommentsRoom,
  getRiskQuestionnaire,
  inviteRecipients,
  listQuestionnaires,
  removeRecipients,
  sendManualReminder,
  submitManualQuestionnaire,
  submitManualQuestionnaireActivity,
  updateManualQuestion,
  updateQuestionnaire,
} from '@/gen/questionnaire/v2/questionnaire-QuestionnaireService_connectquery'
import {
  ListQuestionnairesResponse,
  ListQuestionnairesResponseSchema,
  QuestionnaireService,
} from '@/gen/questionnaire/v2/questionnaire_pb'

import { useTrackCallback } from '@/lib/analytics/events'
import { defaultTransport } from '@/lib/connect/connect-transport'

export const useUpdateManualQuestion = () => {
  return useMutation(updateManualQuestion)
}

export const useSubmitManualQuestionnaire = () => {
  const queryClient = useQueryClient()
  return useMutation(submitManualQuestionnaire, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListQuestionnairesQueryKey() })
      queryClient.invalidateQueries({ queryKey: getInfoQuestionnaireQueryKey() })
    },
  })
}

export const useSubmitManualQuestionnaireActivity = () => {
  const queryClient = useQueryClient()

  return useMutation(submitManualQuestionnaireActivity, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getInfoQuestionnaireQueryKey() })
    },
  })
}

export const getAssessorListQuestionnairesQueryKey = (
  companyId?: Company['id'],
  ongoingAssessmentId?: string,
) =>
  createConnectQueryKey({
    transport: defaultTransport,
    schema: QuestionnaireService.method.listQuestionnaires,
    input: { companyId, ongoingAssessmentId },
    cardinality: 'finite',
  })

export const useListAssessorQuestionnaires = ({
  companyId,
  ongoingAssessmentId,
  refetchInterval,
  enabled = true,
}: {
  companyId?: Company['id']
  ongoingAssessmentId?: string
  refetchInterval?: number
  enabled?: boolean
}) => {
  return useQuery(
    listQuestionnaires,
    {
      companyId,
      ongoingAssessmentId,
    },
    {
      ...(refetchInterval ? { refetchInterval } : null),
      enabled,
    },
  )
}

type PendingLoad = {
  assessmentId: string
  resolve: (value: ListQuestionnairesResponse) => void
  reject: (error: unknown) => void
}

const questionnaireClient = createClient(QuestionnaireService, defaultTransport)
const pendingLoads: PendingLoad[] = []
let batchWindowTimer: ReturnType<typeof setTimeout> | undefined
let draining = false

const settleBatch = (
  batch: PendingLoad[],
  results: Map<string, ListQuestionnairesResponse> | undefined,
  error?: unknown,
) => {
  for (const { assessmentId, resolve, reject } of batch) {
    if (results === undefined) {
      reject(error)
      continue
    }
    const value = results.get(assessmentId)
    if (value) resolve(value)
    else reject(new Error(`Batch result is missing ${assessmentId}`))
  }
}

const runBatch = async (batch: PendingLoad[]) => {
  // The same id can be queued twice in one window.
  const ongoingAssessmentIds = [...new Set(batch.map(({ assessmentId }) => assessmentId))]
  try {
    const { assessments } = await questionnaireClient.listAssessmentQuestionnaires({
      ongoingAssessmentIds,
    })
    settleBatch(
      batch,
      new Map(
        assessments.map(({ ongoingAssessmentId, questionnaires }) => [
          ongoingAssessmentId,
          create(ListQuestionnairesResponseSchema, { questionnaires }),
        ]),
      ),
    )
  } catch (error) {
    settleBatch(batch, undefined, error)
  }
}

// One drain at a time, so a window that opens while a batch is in flight waits instead of
// adding another 3 calls.
const drainPendingLoads = async () => {
  draining = true
  try {
    while (pendingLoads.length > 0) {
      const queued = pendingLoads.splice(0)
      const uniqueIds = [...new Set(queued.map(({ assessmentId }) => assessmentId))]
      const waveSize =
        ASSESSMENT_QUESTIONNAIRES_BATCH_SIZE * ASSESSMENT_QUESTIONNAIRES_MAX_CONCURRENT_BATCHES
      for (let i = 0; i < uniqueIds.length; i += waveSize) {
        const waveIds = uniqueIds.slice(i, i + waveSize)
        const batches: string[][] = []
        for (let j = 0; j < waveIds.length; j += ASSESSMENT_QUESTIONNAIRES_BATCH_SIZE) {
          batches.push(waveIds.slice(j, j + ASSESSMENT_QUESTIONNAIRES_BATCH_SIZE))
        }
        await Promise.all(
          batches.map((ids) => {
            const idSet = new Set(ids)
            return runBatch(queued.filter(({ assessmentId }) => idSet.has(assessmentId)))
          }),
        )
      }
    }
  } finally {
    draining = false
    if (pendingLoads.length > 0) scheduleDrain()
  }
}

const scheduleDrain = () => {
  if (draining || batchWindowTimer !== undefined) return
  batchWindowTimer = setTimeout(() => {
    batchWindowTimer = undefined
    void drainPendingLoads()
  }, ASSESSMENT_QUESTIONNAIRES_BATCH_WINDOW_MS)
}

const loadAssessmentQuestionnaires = (assessmentId: string) =>
  new Promise<ListQuestionnairesResponse>((resolve, reject) => {
    pendingLoads.push({ assessmentId, resolve, reject })
    scheduleDrain()
  })

const assessmentQuestionnairesQueryOptions = (assessmentId: string) => ({
  queryKey: getAssessorListQuestionnairesQueryKey(undefined, assessmentId),
  queryFn: () => loadAssessmentQuestionnaires(assessmentId),
})

/** Questionnaires for one assessment, fetched with the other rows mounted in the same window. */
export const useAssessmentQuestionnaires = ({
  assessmentId,
  enabled = true,
}: {
  assessmentId: string
  enabled?: boolean
}) => useTanstackQuery({ ...assessmentQuestionnairesQueryOptions(assessmentId), enabled })

/**
 * Questionnaires for many assessments. Fresh cache entries are reused; a failed batch resolves
 * those rows to null.
 */
export const fetchAssessmentsQuestionnaires = (queryClient: QueryClient, assessmentIds: string[]) =>
  Promise.all(
    assessmentIds.map(async (assessmentId) => {
      try {
        const data = await queryClient.fetchQuery({
          ...assessmentQuestionnairesQueryOptions(assessmentId),
          staleTime: ASSESSMENT_QUESTIONNAIRES_BULK_STALE_TIME_MS,
        })
        return { assessmentId, data }
      } catch (error) {
        console.error(`Failed to fetch questionnaires for assessment ${assessmentId}:`, error)
        return { assessmentId, data: null }
      }
    }),
  )

export const useUpdateQuestionnaire = () => {
  const queryClient = useQueryClient()
  return useMutation(updateQuestionnaire, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListQuestionnairesQueryKey() })
      // Sharing seeds control thread activities, so refresh the per-control
      // timeline and control queries that render them.
      queryClient.invalidateQueries({ queryKey: getControlTimelineQueryKey() })
      queryClient.invalidateQueries({ queryKey: getControlQueryKey() })
    },
  })
}

export const useGetRiskQuestionnaire = ({ questionnaireId }: { questionnaireId: string }) => {
  return useQuery(getRiskQuestionnaire, { questionnaireId })
}

export const useGetInfoQuestionnaire = ({ questionnaireId }: { questionnaireId: string }) => {
  return useQuery(
    getInfoQuestionnaire,
    { questionnaireId },
    { refetchInterval: REFETCH_INTERVAL_MS },
  )
}

export const useInviteRecipient = () => {
  const queryClient = useQueryClient()

  return useMutation(inviteRecipients, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListQuestionnairesQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetCompanyQueryKey() })
    },
  })
}

export const useRemoveRecipients = () => {
  const queryClient = useQueryClient()
  const trackRecipientsRemove = useTrackCallback('questionnaire.recipients.remove')

  return useMutation(removeRecipients, {
    onSuccess: (_data, variables) => {
      trackRecipientsRemove({
        orgCompanyId: variables.orgCompanyId,
        recipient_count: variables.emails?.length ?? 0,
      })
      queryClient.invalidateQueries({ queryKey: getListQuestionnairesQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetCompanyQueryKey() })
    },
  })
}

export const useSendManualReminder = () => {
  return useMutation(sendManualReminder)
}

export const useDeleteQuestionnaire = () => {
  const queryClient = useQueryClient()
  const trackQuestionnaireDelete = useTrackCallback('questionnaire.delete')

  return useMutation(deleteQuestionnaire, {
    onSuccess: (_data, variables) => {
      trackQuestionnaireDelete({ questionnaireId: variables.questionnaireId })
      queryClient.invalidateQueries({ queryKey: getListQuestionnairesQueryKey() })
    },
  })
}

export const getInfoQuestionnaireQueryKey = (questionnaireId?: string) =>
  createConnectQueryKey({
    schema: QuestionnaireService.method.getInfoQuestionnaire,
    input: { questionnaireId },
    transport: defaultTransport,
    cardinality: 'finite',
  })

// ID tokens can only enter rooms that already exist, so the room has to be created
// before mounting a RoomProvider for it. The server returns the room id to connect to.
export const useGetPortalCommentsRoom = (orgCompanyId?: string) =>
  useQuery(getPortalCommentsRoom, { orgCompanyId }, { enabled: !!orgCompanyId })
